import { ThemeEvents } from '@theme/events';

class DirectCheckoutButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.errorElement = this.querySelector('.direct-checkout-error');

    if (!this.button || this.button.dataset.directCheckoutReady === 'true') return;

    this.button.dataset.directCheckoutReady = 'true';
    this.isAvailable = !this.button.disabled;
    this.abortController = new AbortController();

    this.button.addEventListener('click', this.handleClick, { signal: this.abortController.signal });

    const eventTarget = this.closest('.shopify-section, dialog, product-card');
    eventTarget?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleVariantUpdate = (event) => {
    const productForm = this.closest('product-form-component');
    const eventProductId = event.detail?.data?.productId;

    if (eventProductId && productForm?.dataset.productId !== eventProductId) return;

    this.isAvailable = Boolean(event.detail?.resource?.available);

    if (!this.isLoading && this.button) {
      this.button.disabled = !this.isAvailable;
    }
  };

  handleClick = async () => {
    const form = this.closest('form');
    if (!form || !this.button || this.button.disabled) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.hideError();
    this.setLoading(true);

    try {
      const formData = new FormData(form);

      if (!formData.get('id')) throw new Error('Please select a product option.');
      if (!formData.get('quantity')) formData.set('quantity', '1');

      if (this.dataset.requiresSellingPlan === 'true' && !formData.get('selling_plan')) {
        throw new Error('Please select a subscription option.');
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to continue to checkout.');
      }

      const checkoutForm = document.createElement('form');
      checkoutForm.method = 'post';
      checkoutForm.action = Theme.routes.cart_url;
      checkoutForm.hidden = true;

      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'Checkout';

      checkoutForm.appendChild(checkoutInput);
      document.body.appendChild(checkoutForm);
      checkoutForm.submit();
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Unable to continue to checkout.');
      this.setLoading(false);
    }
  };

  setLoading(isLoading) {
    if (!this.button) return;
    this.isLoading = isLoading;
    this.button.disabled = isLoading || !this.isAvailable;
    this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.textContent = '';
    this.errorElement.hidden = true;
  }
}

if (!customElements.get('direct-checkout-button')) {
  customElements.define('direct-checkout-button', DirectCheckoutButton);
}
